
import { MoveRight } from 'lucide-react';
import React, { ClipboardEvent, createRef, FormEvent, useEffect, useState } from 'react';
import Button from '../Button';

export type TwoFactorAuthProps = {
    handleConfirmVerification: (value: string) => void;
    handleResendTwoFa: () => void;
    isMutating: boolean
}

const TwoFactorAuth = ({ handleConfirmVerification, handleResendTwoFa, isMutating }: TwoFactorAuthProps) => {

    const initialOtpState = Array(6).fill('');
    const [otp, setOtp] = useState(initialOtpState);
    const [resendTimeout, setResendTimeout] = useState<any>(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    const inputRefs = Array<any>(6)
        .fill(6)
        .map(() => createRef<any>());

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newOtp = [...otp];
        newOtp[index] = e.target.value;
        setOtp(newOtp);
        if (e.target.value.length === 1 && index < 5) {
            inputRefs[index + 1].current.focus();
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLFormElement>) => {
        event.preventDefault();
        const pasteData = event.clipboardData.getData('text').slice(0, 6).split('');
        if (pasteData.length === 6) {
            setOtp(pasteData);
            inputRefs[5].current.focus();
        }
    };

    useEffect(() => {
        if (!resendTimeout) {
            // Focus on the OTP input field
            inputRefs[0].current.focus();

            // Set a timeout before allowing the next resend request (e.g., 30 seconds)
            const timeoutId = setTimeout(() => {
                setResendTimeout(null); // Clear the timeout after it expires
            }, 30000); // 30 seconds
            setResendTimeout(timeoutId);
            setSecondsRemaining(30); // Reset the countdown timer
        }
    }, []);

    useEffect(() => {
        if (resendTimeout) {
            const countdownInterval = setInterval(() => {
                setSecondsRemaining((prevSeconds) => {
                    if (prevSeconds === 0) {
                        clearInterval(countdownInterval);
                        return 0;
                    }
                    return prevSeconds - 1;
                });
            }, 1000); // Update every second

            return () => clearInterval(countdownInterval);
        }
    }, [resendTimeout]);

    const handleResendCode = () => {
        if (!resendTimeout) {
            // Logic to send new OTP to the user (
            handleResendTwoFa();
            // Focus on the OTP input field after resend
            inputRefs[0].current.focus();
            setOtp(initialOtpState);
            // Set a timeout before allowing the next resend request (e.g., 30 seconds)
            const timeoutId = setTimeout(() => {
                setResendTimeout(null); // Clear the timeout after it expires
            }, 30000); // 30 seconds
            setResendTimeout(timeoutId);
            setSecondsRemaining(30); // Reset the countdown timer
        }
    };

    const handleBackspace = (element: HTMLInputElement, index: number) => {
        if (index > 0 && element.value.length === 0) {
            inputRefs[index - 1].current.focus();
        }
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();

        handleConfirmVerification(otp.join(''));
    };

    return (
        <div className="text-center">
            <form
                onSubmit={handleSubmit}
                onPaste={handlePaste}
            >
                <div className="grid grid-cols-6 gap-4 mb-5 p-[2px_0] max-w-[425px]">
                    {otp.map((_, index) => (
                        <input
                            key={index}
                            ref={inputRefs[index]}
                            type="text"
                            maxLength={1}
                            value={otp[index] || ''}
                            onChange={(e) => handleChange(e, index)}
                            autoComplete="off"
                            onKeyDown={(e) =>
                                e.key === 'Backspace' && handleBackspace(e.target as HTMLInputElement, index)
                            }
                            className={`w-full h-full text-center font-roboto text-[27px] text-[#3ed696] font-medium rounded-lg max-w-[56px] max-h-[56px] p-0 caret-[#272d3c] aspect-[1/1] border-none outline outline-1 outline-black/25 ${otp[index] ? 'outline outline-2 outline-[#3ed696] border-none' : ''}`}
                        />
                    ))}
                </div>
                <Button
                    type="submit"
                    className="w-full text-sm"
                    loading={isMutating}
                    disabled={otp.join('').length !== 6}
                    rightIcon={<MoveRight size={18} />}
                >
                    Confirm
                </Button>
            </form>
            <div className="flex justify-center items-center mt-[25px] text-[14px] flex gap-1">
                Need a new code?
                <Button
                    onClick={handleResendCode}
                    disabled={resendTimeout}
                    variant="link"
                    size="link"
                >
                    {resendTimeout ? `Resend Code (${secondsRemaining}s)` : 'Resend Code'}
                </Button>
            </div>
        </div>
    );
};

export default TwoFactorAuth;
